/**
 * Who the viewer is, what they may do, and what the product is called.
 *
 * Permission checks in the UI are for affordance only: they decide what to
 * render, never what is allowed. Every rule is enforced server-side, because a
 * hidden UI element is not a licence control.
 */

import { createContext, useContext, useEffect, type ReactNode } from 'react'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { API_BASE, api, isSignedIn, type ClientConfig, type Me, type Viewer } from './api'

interface SessionValue {
  config: ClientConfig | undefined
  viewer: Viewer | undefined
  me: Me | undefined
  loading: boolean
  can: (permission: string) => boolean
  hasFeature: (key: string) => boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  signInError: string | null
  signingIn: boolean
}

const SessionContext = createContext<SessionValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  const configQuery = useQuery({
    queryKey: ['config'],
    queryFn: () => api.get<ClientConfig>('/config'),
    staleTime: 5 * 60 * 1000,
  })

  const viewerQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<Viewer>('/auth/me'),
    retry: false,
  })

  const signInMutation = useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api.post<Me>('/auth/login', input),
    onSuccess: (me) => {
      queryClient.setQueryData(['me'], me)
      queryClient.invalidateQueries()
    },
  })

  const signOutMutation = useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSuccess: () => {
      queryClient.clear()
      const base = configQuery.data?.base_path || ''
      window.location.href = base ? `${base}/` : '/'
    },
  })

  // The displayed name is configuration, never a literal in this source tree.
  const productName = configQuery.data?.product_name
  useEffect(() => {
    if (productName) {
      document.title = productName
    }
  }, [productName])

  const viewer = viewerQuery.data
  const me = isSignedIn(viewer) ? viewer : undefined

  // A session the reader marked as ephemeral at sign-in (a shared reading
  // room workstation) is ended when the tab goes away. `keepalive` lets the
  // request outlive the page. The seat it held is released with it.
  useEffect(() => {
    let ephemeral = false
    try {
      ephemeral = sessionStorage.getItem('jl.ephemeral') === '1'
    } catch {
      ephemeral = false
    }
    if (!ephemeral || !me) return
    const end = () => {
      void fetch(`${API_BASE}/auth/logout`, { method: 'POST', credentials: 'same-origin', keepalive: true })
    }
    window.addEventListener('pagehide', end)
    return () => window.removeEventListener('pagehide', end)
  }, [me])

  const value: SessionValue = {
    config: configQuery.data,
    viewer,
    me,
    loading: configQuery.isLoading || viewerQuery.isLoading,
    can: (permission) => !!viewer?.permissions?.includes(permission),
    hasFeature: (key) => !!(viewer?.features ?? configQuery.data?.features)?.[key],
    signIn: async (email, password) => {
      await signInMutation.mutateAsync({ email, password })
    },
    signOut: async () => {
      await signOutMutation.mutateAsync()
    },
    signInError: signInMutation.error ? (signInMutation.error as Error).message : null,
    signingIn: signInMutation.isPending,
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext)
  if (!value) {
    throw new Error('useSession must be used inside a SessionProvider')
  }
  return value
}
